"use client";

import { forwardRef } from "react";

import { ButtonContent } from "./components/ButtonContent";
import { AccentStyledButton } from "./components/styled/AccentStyledButton";
import { StyledLink } from "./components/styled/StyledLink";
import { ButtonVariant } from "./enums";
import { useButton } from "./hooks/useButton";
import type { ButtonProps } from "./interfaces";

/**
 * AccentButton is a styled button component that provides visual emphasis through accent colors.
 * It can render as either a button or a link, supporting various content types including text,
 * icons, and avatars, with loading and disabled states.
 *
 * @component
 * @example
 * <AccentButton
 *    href="https://www.google.com"
 *    target="_blank"
 *    size="x-small"
 *    label="Accent Button (x-small) Link"
 *    avatar={{
 *      type: "icon",
 *      icon: { name: "Cube2_12_Fill" },
 *    }}
 *    icon12pxLeft="Cube2_12_Fill"
 *    icon12pxRight="Sparkle2_12_Outline"
 *    icon18pxLeft="Cube2_18_Fill"
 *    icon18pxRight="Sparkle2_18_Outline"
 *    shortcutKey={{
 *      variant: "sequence",
 *      keys: [{ type: "letter", value: "⌘" }],
 *    }}
 *    onClick={() => console.log("clicked")}
 * />
 * <AccentButton
 *    size="small"
 *    label="Accent Button (small)"
 *    avatar={{
 *      type: "image",
 *      src: "images/avatar.png",
 *      alt: "Dechipher",
 *      color: "warning",
 *    }}
 *    icon12pxLeft="Cube2_12_Fill"
 *    icon12pxRight="Sparkle2_12_Outline"
 *    icon18pxLeft="Cube2_18_Fill"
 *    icon18pxRight="Sparkle2_18_Outline"
 * />
 *
 * @param props - Component props
 * @param  size - Button size variant ("x-small" | "small" | "medium" | "large" | "x-large")
 * @param  label - Text content of the button
 * @param  disabled - Whether the button is disabled
 * @param  loading - Whether to show loading state and loading indicator
 * @param  avatar - Avatar element to display within the button (optional)
 * @example
 * // first priority is type (icon, image, logo, initial)
 * // according to type rest of the props will be asked feel free to pass as the design definition from figma(you will get type hints)
 * avatar={{
 *    type: "icon",
 *    icon: { name: "Cube2_12_Fill" },
 * }}
 *
 * avatar={{
 *      type: "image",
 *      src: "images/avatar.png",
 *      alt: "Dechipher",
 *      color: "warning",
 * }}
 *
 *
 * @param  icon12pxLeft - 12px sized icon to display on the left (optional)
 * @example
 * // select the name from type suggestions if not found then add it in the assets folder and then import it in the index.tsx file
 * // follow the name convention of the icons in the assets folder
 *  icon12pxRight="Sparkle2_12_Outline"
 *  icon12pxLeft="Cube2_12_Fill"
 *  icon18pxRight="Sparkle2_18_Outline"
 *  icon18pxLeft="Cube2_18_Fill"
 *
 * @param  icon12pxRight - 12px sized icon to display on the right (optional) ↑
 * @param  icon18pxLeft - 18px sized icon to display on the left (optional) ↑
 * @param  icon18pxRight - 18px sized icon to display on the right (optional) ↑
 * @param  shortcutKey - Short key to show on the button (optional)
 * @example
 * // first priority is variant sequence or stack
 * // second priority is keys array order and type (letter or text) and its value
 * // you will get type hints for letter type key
 * // other config will be applied to the shortcut key accordingly
 *  shortcutKey={{
 *    variant: "sequence",
 *    keys: [{ type: "letter", value: "⌘" }],
 *  }}
 * @param  onClick - Click event handler (optional)
 * @param  onKeyDown - Key down event handler (optional)
 * @param  href - URL to navigate to when button is clicked (renders as link) (optional)
 * @param  target - Target attribute for link variant ("_self" | "_blank" | "_parent" | "_top") (optional)
 * @param  ref - Forward ref for the button element (optional)
 *
 * @returns  An accent-styled button or link component
 */

const AccentButton = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      size = "medium",
      label,
      disabled = false,
      loading = false,
      avatar,
      icon12pxLeft,
      icon12pxRight,
      icon18pxLeft,
      icon18pxRight,
      onClick,
      href,
      target,
      shortcutKey,
      ...restProps
    },
    ref,
  ) => {
    // Common props for ButtonContent
    const contentProps = {
      variant: ButtonVariant.ACCENT,
      loading,
      avatar,
      icon12pxLeft,
      icon18pxLeft,
      label,
      icon18pxRight,
      icon12pxRight,
      shortcutKey,
      disabled,
    };

    // Common button props
    const buttonProps = {
      ref,
      $size: size,
      $loading: loading,
      $disabled: disabled,
      disabled,
      ...restProps,
    };

    const { handleClick } = useButton({
      onClick,
      loading,
    });

    // If using as a link
    if (href) {
      return (
        <StyledLink href={href} target={target}>
          <AccentStyledButton {...buttonProps} onClick={handleClick}>
            <ButtonContent {...contentProps} />
          </AccentStyledButton>
        </StyledLink>
      );
    }

    // If using as a button
    return (
      <AccentStyledButton {...buttonProps} onClick={handleClick}>
        <ButtonContent {...contentProps} />
      </AccentStyledButton>
    );
  },
);

AccentButton.displayName = "AccentButton";

export default AccentButton;
